import { useState, useCallback } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Avatar, Dropdown } from 'antd'
import {
  DashboardOutlined,
  LineChartOutlined,
  DatabaseOutlined,
  RobotOutlined,
  FileTextOutlined,
  UserOutlined,
  SunOutlined,
  MoonOutlined,
  BellOutlined,
  StarOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { useThemeStore } from '../../stores/themeStore'
import SpritePet from '../common/SpritePet/SpritePet'
import { PET_ENABLED_KEY } from '../common/SpritePet/types'
import './AppLayout.css'

const { Sider, Content } = Layout

const AppLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const { theme, toggleTheme } = useThemeStore()

  // 精灵宠物开关状态（默认关闭）
  const [petEnabled, setPetEnabled] = useState(() => {
    return localStorage.getItem(PET_ENABLED_KEY) === 'true'
  })
  const [galleryOpen, setGalleryOpen] = useState(false)

  const handlePetEnabledChange = useCallback((enabled: boolean) => {
    setPetEnabled(enabled)
    localStorage.setItem(PET_ENABLED_KEY, String(enabled))
  }, [])

  // 主菜单项
  const menuItems: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
    },
    {
      key: '/analysis',
      icon: <LineChartOutlined />,
      label: '分析工作台',
    },
    {
      key: '/data',
      icon: <DatabaseOutlined />,
      label: '数据探索',
    },
    {
      key: '/ai-explore',
      icon: <RobotOutlined />,
      label: 'AI 探索',
    },
    {
      key: '/alerts',
      icon: <BellOutlined />,
      label: '预警通知',
    },
    {
      key: '/reports',
      icon: <FileTextOutlined />,
      label: '报告中心',
    },
  ]

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
    },
    {
      key: 'theme',
      icon: theme === 'dark' ? <MoonOutlined /> : <SunOutlined />,
      label: theme === 'dark' ? '深色主题' : '浅色主题',
      onClick: () => {
        toggleTheme()
      },
    },
    {
      key: 'sprite-pet',
      icon: <StarOutlined />,
      label: '魔法精灵',
      onClick: () => {
        setGalleryOpen(true)
      },
    },
    {
      type: 'divider',
    },
  ]

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key)
  }

  return (
    <Layout className="app-layout">
      <Sider
        className="app-sider"
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        width={180}
        collapsedWidth={64}
        trigger={null}
        theme={theme === 'dark' ? 'dark' : 'light'}
      >
        <div className="sider-logo">
          {collapsed ? 'VOC' : 'VOC洞察平台'}
        </div>
        <Menu
          theme={theme === 'dark' ? 'dark' : 'light'}
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          inlineCollapsed={collapsed}
          className="main-menu"
        />
        <div className="sider-user-section">
          <Dropdown menu={{ items: userMenuItems }} placement="topRight" trigger={['click']}>
            <div className={`sider-user-info ${collapsed ? 'collapsed' : ''}`}>
              <Avatar className="app-avatar" size={collapsed ? 36 : 32} icon={<UserOutlined />} />
              {!collapsed && <span className="sider-user-name">用户</span>}
            </div>
          </Dropdown>
        </div>
      </Sider>

      {/* 抽屉拉手样式的折叠按钮 - 放在侧边栏外部 */}
      <div
        className="drawer-handle"
        onClick={() => setCollapsed(!collapsed)}
        style={{ left: collapsed ? 64 : 180 }}
      >
        {/* 拉手纹理 - 三条竖线 */}
        <div className="drawer-handle-line" />
        <div className="drawer-handle-line" />
        <div className="drawer-handle-line" />
      </div>
      <Layout
        className="app-content-layout"
        style={{ marginLeft: collapsed ? 64 : 180 }}
      >
        <Content className="app-content">
          <Outlet />
        </Content>
      </Layout>
      <SpritePet
        enabled={petEnabled}
        externalGalleryOpen={galleryOpen}
        onExternalGalleryClose={() => setGalleryOpen(false)}
        onPetEnabledChange={handlePetEnabledChange}
      />
    </Layout>
  )
}

export default AppLayout
